import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Skeleton, TextField, Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import Icon from '../../../components/icon';
import api from '../../../utils/axiosConfig';
import './input-produksi-detail.scss';

interface WorkOrder {
  id: number;
  qty: number;
  satuan?: string;
  proses?: string;
  kode?: string;
  product?: string;
}

interface Proses {
  id: number;
  proses?: string | null;
  is_process_configured?: boolean;
  qty: number;
  qty_hasil: number;
  satuan?: string;
  tgl_awal: string | null;
  tgl_realisasi: string | null;
  tgl_selesai: string | null;
  urutan: number;
  is_input_persen: number;
  is_bordir: boolean;
  requires_stitch: boolean;
}

interface HistoryProses {
  id?: number | string;
  qty?: number;
  stitch?: number;
  is_bordir?: boolean | number;
  pic?: string;
  tgl_selesai?: string;
}

const PENJAHIT_PROCESSES = ['QC', 'Potong', 'Penjahit', 'Bordir', 'BHP'];
const pad = (value: number) => String(value).padStart(2, '0');
const inputDate = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const createDefaultPeriod = () => {
  const today = new Date();
  return {
    awal: inputDate(new Date(today.getFullYear(), today.getMonth() - 2, 1)),
    akhir: inputDate(today),
  };
};
const formatNumber = (value: unknown) => Number(value || 0).toLocaleString('id-ID', { maximumFractionDigits: 2 });
const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('id-ID', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(date);
};
const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date);
};
const getErrorMessage = (error: any, fallback: string) => error?.response?.data?.message || error?.message || fallback;
const getStitchError = (payload: any) => {
  const error = payload?.errors?.stitch;
  if (Array.isArray(error)) return error.join(' ');
  return error ? String(error) : '';
};
const normalizedProcessName = (value: unknown) => String(value ?? '').trim().toLowerCase();

export default function Detail({ routeParams }: { routeParams?: string }) {
  const params = useParams();
  const station = useMemo(() => decodeURIComponent(String(routeParams || params.id || '')).trim(), [routeParams, params.id]);
  const defaults = useMemo(createDefaultPeriod, []);
  const [awal, setAwal] = useState(defaults.awal);
  const [akhir, setAkhir] = useState(defaults.akhir);
  const [appliedPeriod, setAppliedPeriod] = useState(defaults);
  const [periodError, setPeriodError] = useState('');
  const [selectedWO, setSelectedWO] = useState<WorkOrder | null>(null);
  const [selectedProcess, setSelectedProcess] = useState<Proses | null>(null);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [processes, setProcesses] = useState<Proses[]>([]);
  const [history, setHistory] = useState<HistoryProses[]>([]);
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState('');
  const [stitch, setStitch] = useState('');
  const [workOrderLoading, setWorkOrderLoading] = useState(true);
  const [processLoading, setProcessLoading] = useState(false);
  const [processNotice, setProcessNotice] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [workOrderError, setWorkOrderError] = useState('');
  const scanRef = useRef<HTMLInputElement>(null);
  const idleTimer = useRef<number | null>(null);

  const focusScanner = useCallback(() => {
    window.setTimeout(() => scanRef.current?.focus(), 80);
  }, []);

  useEffect(() => { setStitch(''); }, [selectedProcess?.id]);

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(() => {
        const activeTag = document.activeElement?.tagName;
        const userIsEditing = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT';
        if (!Swal.isVisible() && !userIsEditing) focusScanner();
      }, 60_000);
    };
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer));
    resetIdleTimer();
    return () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
    };
  }, [focusScanner]);

  const isProcessAllowed = useCallback((processName?: string | null) => {
    const normalizedProcess = normalizedProcessName(processName);
    if (!normalizedProcess) return false;
    const normalizedStation = normalizedProcessName(station);
    if (!normalizedStation) return true;
    if (normalizedStation === 'penjahit') {
      return PENJAHIT_PROCESSES.some(value => normalizedProcessName(value) === normalizedProcess);
    }
    return normalizedStation === normalizedProcess;
  }, [station]);

  const getHistory = useCallback(async (process: Proses) => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/production/work-order/history-plan/${process.id}`);
      if (data?.success === false) throw new Error(data.message || 'Riwayat proses gagal dimuat.');
      setHistory(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      setHistory([]);
      Swal.fire({ title: 'Riwayat tidak tersedia', text: getErrorMessage(error, 'Gagal mengambil riwayat proses.'), icon: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const getProcesses = useCallback(async (workOrderId: number, preferredProcessId?: number) => {
    setProcessLoading(true);
    setProcesses([]);
    setHistory([]);
    setProcessNotice('');
    try {
      const { data } = await api.get(`/production/work-order/list-proses-plan/${workOrderId}`);
      if (data?.success === false) throw new Error(data.message || 'Data proses gagal dimuat.');
      const rows: Proses[] = Array.isArray(data?.data) ? data.data : [];
      const unconfiguredCount = rows.filter(process => process.is_process_configured === false || !normalizedProcessName(process.proses)).length;
      const allowed = rows.filter(process => process.is_process_configured !== false && isProcessAllowed(process.proses));
      if (unconfiguredCount > 0) {
        setProcessNotice(`${unconfiguredCount} plan kehilangan referensi nama proses dan tidak ditampilkan. Silakan periksa konfigurasi proses Work Order.`);
      }
      setProcesses(allowed);
      const nextSelected = allowed.find((process: Proses) => process.id === preferredProcessId) || (allowed.length === 1 ? allowed[0] : null);
      setSelectedProcess(nextSelected);
      if (nextSelected) getHistory(nextSelected);
    } catch (error) {
      setSelectedProcess(null);
      Swal.fire({ title: 'Proses tidak tersedia', text: getErrorMessage(error, 'Gagal mengambil data proses.'), icon: 'error' });
    } finally {
      setProcessLoading(false);
    }
  }, [getHistory, isProcessAllowed]);

  const selectWorkOrder = useCallback((workOrder: WorkOrder) => {
    setSelectedWO(workOrder);
    setSelectedProcess(null);
    setScanValue('');
    setScanError('');
    getProcesses(workOrder.id);
  }, [getProcesses]);

  const getWorkOrders = useCallback(async () => {
    setWorkOrderLoading(true);
    setWorkOrderError('');
    try {
      const { data } = await api.get('/production/work-order/list-order-plan', { params: appliedPeriod });
      if (data?.success === false) throw new Error(data.message || 'Data work order gagal dimuat.');
      const rows = Array.isArray(data?.data) ? data.data : [];
      setWorkOrders(rows);
    } catch (error) {
      setWorkOrders([]);
      setWorkOrderError(getErrorMessage(error, 'Gagal mengambil data work order.'));
    } finally {
      setWorkOrderLoading(false);
      focusScanner();
    }
  }, [appliedPeriod, focusScanner]);

  useEffect(() => { getWorkOrders(); }, [getWorkOrders]);

  const visibleWorkOrders = useMemo(() => {
    const keyword = scanValue.trim().toLowerCase();
    if (!keyword) return workOrders;
    return workOrders.filter(workOrder => [workOrder.id, workOrder.kode, workOrder.proses, workOrder.product]
      .some(value => String(value ?? '').toLowerCase().includes(keyword)));
  }, [scanValue, workOrders]);

  const handleScan = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const keyword = scanValue.trim().toLowerCase();
    if (!keyword) return setScanError('Scan atau masukkan kode WO terlebih dahulu.');
    const exact = workOrders.filter(workOrder => String(workOrder.kode ?? '').toLowerCase() === keyword || String(workOrder.id) === keyword);
    const result = exact.length === 1 ? exact[0] : visibleWorkOrders.length === 1 ? visibleWorkOrders[0] : null;
    if (!result) return setScanError(visibleWorkOrders.length ? 'Ditemukan beberapa WO. Pilih salah satu dari daftar.' : 'Kode WO tidak ditemukan pada periode ini.');
    selectWorkOrder(result);
  };

  const applyPeriod = () => {
    if (!awal || !akhir) return setPeriodError('Tanggal awal dan akhir wajib diisi.');
    if (awal > akhir) return setPeriodError('Tanggal awal tidak boleh melebihi tanggal akhir.');
    setPeriodError(''); setSelectedWO(null); setSelectedProcess(null); setProcesses([]); setHistory([]); setProcessNotice(''); setScanValue('');
    setAppliedPeriod({ awal, akhir });
  };

  const refreshSelectedProcess = useCallback(async (processId?: number) => {
    if (selectedWO) await getProcesses(selectedWO.id, processId);
  }, [getProcesses, selectedWO]);

  const askNik = async (action: string) => {
    const result = await Swal.fire({
      title: 'Scan NIK Operator',
      text: `${action} ${selectedProcess?.proses || 'proses'} pada WO ${selectedWO?.kode || selectedWO?.id || '-'}`,
      input: 'text', inputPlaceholder: 'Scan atau masukkan NIK...', showCancelButton: true,
      confirmButtonText: 'Lanjutkan', cancelButtonText: 'Batal', focusConfirm: false,
      inputAttributes: { autocomplete: 'off', autocapitalize: 'off' },
      inputValidator: value => value?.trim() ? undefined : 'NIK tidak boleh kosong.',
    });
    return result.isConfirmed ? String(result.value).trim() : null;
  };

  const handleStartProcess = async () => {
    if (!selectedProcess || actionLoading) return;
    const process = selectedProcess;
    const nik = await askNik('Mulai');
    if (!nik) return focusScanner();
    setActionLoading(true);
    try {
      const { data } = await api.post('/production/wo-plan/mulai-proses', { pic: nik, wo_plan_id: process.id });
      if (data?.success === false) throw new Error(data.message || 'Proses gagal dimulai.');
      await refreshSelectedProcess(process.id);
      await Swal.fire({ title: 'Proses dimulai', text: `${process.proses} siap dikerjakan.`, icon: 'success', timer: 1200, showConfirmButton: false });
    } catch (error) {
      await Swal.fire({ title: 'Gagal memulai', text: getErrorMessage(error, 'Gagal memulai proses.'), icon: 'error' });
    } finally { setActionLoading(false); focusScanner(); }
  };

  const submitResult = async (nik: string, process: Proses, subProcess: string | null, qty: number, isPercentage: number) => {
    setActionLoading(true);
    try {
      const { data } = await api.post('/production/wo-plan/selesai-proses', {
        pic: nik, wo_plan_id: process.id, qty, is_persen: isPercentage, sub_proses: subProcess,
      });
      if (data?.success === false) throw new Error(data.message || 'Hasil proses gagal disimpan.');
      await refreshSelectedProcess(process.id);
      await Swal.fire({ title: 'Hasil tersimpan', text: `${process.proses}${subProcess ? ` — ${subProcess}` : ''} berhasil dicatat.`, icon: 'success', timer: 1300, showConfirmButton: false });
    } catch (error) {
      await Swal.fire({ title: 'Gagal menyimpan', text: getErrorMessage(error, 'Gagal menyimpan hasil proses.'), icon: 'error' });
    } finally { setActionLoading(false); focusScanner(); }
  };

  const submitResultWithStitch = async (
    nik: string, process: Proses, subProcess: string | null, qty: number, isPercentage: number, context: string,
  ) => {
    const result = await Swal.fire({
      title: 'Jumlah Stitch',
      html: context,
      input: 'number', inputValue: stitch, inputPlaceholder: 'Contoh: 12500',
      inputAttributes: { min: '1', step: '1', inputmode: 'numeric' },
      showCancelButton: true, confirmButtonText: 'Simpan hasil', cancelButtonText: 'Batal',
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: () => {
        const input = Swal.getInput();
        input?.addEventListener('input', event => setStitch((event.target as HTMLInputElement).value));
      },
      inputValidator: value => {
        const parsed = Number(value);
        if (!value || !Number.isInteger(parsed) || parsed <= 0) return 'Jumlah Stitch wajib berupa bilangan bulat lebih dari 0.';
        return undefined;
      },
      preConfirm: async value => {
        const parsedStitch = Number(value);
        setActionLoading(true);
        try {
          const { data } = await api.post('/production/wo-plan/selesai-proses', {
            wo_plan_id: process.id,
            pic: nik,
            qty,
            is_persen: isPercentage,
            sub_proses: subProcess,
            stitch: parsedStitch,
          });
          if (data?.success === false) {
            Swal.showValidationMessage(getStitchError(data) || data?.message || 'Hasil proses gagal disimpan.');
            return false;
          }
          return { stitch: parsedStitch };
        } catch (error: any) {
          const payload = error?.response?.data;
          Swal.showValidationMessage(getStitchError(payload) || getErrorMessage(error, 'Gagal menyimpan hasil proses.'));
          return false;
        } finally {
          setActionLoading(false);
        }
      },
    });
    if (!result.isConfirmed) return focusScanner();
    setStitch('');
    await refreshSelectedProcess(process.id);
    await Swal.fire({ title: 'Hasil tersimpan', text: `${process.proses}${subProcess ? ` — ${subProcess}` : ''} berhasil dicatat.`, icon: 'success', timer: 1300, showConfirmButton: false });
    focusScanner();
  };

  const handleEmbroideryResult = async (nik: string, process: Proses) => {
    const stages = [
      { value: 'Desain', icon: '🎨', color: '#6f42c1' }, { value: 'QC', icon: '🔍', color: '#0d6efd' },
      { value: 'Bordir', icon: '🧵', color: '#198754' }, { value: 'QC Finishing', icon: '✅', color: '#fd7e14' },
    ];
    let selectedStage = '';
    await Swal.fire({
      title: 'Pilih Tahapan Bordir',
      html: `<div class="embroidery-stage-grid">${stages.map(stage => `<button type="button" data-stage="${stage.value}" style="--stage-color:${stage.color}"><span>${stage.icon}</span>${stage.value}</button>`).join('')}</div>`,
      showCancelButton: true, showConfirmButton: false, cancelButtonText: 'Batal',
      didOpen: popup => popup.querySelectorAll<HTMLButtonElement>('[data-stage]').forEach(button => button.addEventListener('click', () => {
        selectedStage = String(button.dataset.stage || '');
        Swal.close();
      })),
    });
    if (!selectedStage) return focusScanner();
    if (process.requires_stitch === true) {
      await submitResultWithStitch(
        nik, process, selectedStage, 25, 1,
        `Tahapan <b>${selectedStage}</b> akan dicatat sebesar <b>25%</b> untuk WO <b>${selectedWO?.kode || selectedWO?.id}</b>.<br>Masukkan Jumlah Stitch:`,
      );
      return;
    }
    const confirmation = await Swal.fire({
      title: `Catat ${selectedStage}?`,
      html: `Tahapan <b>${selectedStage}</b> akan dicatat sebesar <b>25%</b> untuk WO <b>${selectedWO?.kode || selectedWO?.id}</b>.`,
      icon: 'question', showCancelButton: true, confirmButtonText: 'Ya, simpan', cancelButtonText: 'Periksa lagi',
    });
    if (confirmation.isConfirmed) await submitResult(nik, process, selectedStage, 25, 1);
    else focusScanner();
  };

  const handleNormalResult = async (nik: string, process: Proses) => {
    const isPercentage = process.is_input_persen === 1;
    const target = Number(process.qty || 0);
    const resultQty = Number(process.qty_hasil || 0);
    const remaining = Math.max(0, target - resultQty);
    const result = await Swal.fire({
      title: isPercentage ? 'Masukkan Persentase Hasil' : 'Masukkan Quantity Hasil',
      html: `<div class="quantity-context"><span>Target <b>${formatNumber(target)} ${process.satuan || ''}</b></span><span>Sudah tercatat <b>${formatNumber(resultQty)} ${process.satuan || ''}</b></span><span>Sisa <b>${formatNumber(remaining)} ${process.satuan || ''}</b></span></div>`,
      input: 'number', inputPlaceholder: isPercentage ? 'Contoh: 25' : 'Masukkan qty...',
      inputAttributes: { min: '0.01', max: isPercentage ? '100' : '', step: 'any', inputmode: 'decimal' },
      showCancelButton: true, confirmButtonText: 'Simpan hasil', cancelButtonText: 'Batal',
      inputValidator: value => {
        const parsed = Number(value);
        if (!value || !Number.isFinite(parsed) || parsed <= 0) return 'Nilai harus lebih dari 0.';
        if (isPercentage && parsed > 100) return 'Persentase tidak boleh lebih dari 100%.';
        return undefined;
      },
    });
    if (!result.isConfirmed) return focusScanner();
    const qty = Number(result.value);
    const inputInUnits = isPercentage ? (qty / 100) * target : qty;
    if (inputInUnits > remaining) {
      const anomaly = await Swal.fire({
        title: 'Hasil melebihi sisa target',
        html: `Input setara <b>${formatNumber(inputInUnits)} ${process.satuan || ''}</b>, sedangkan sisa target <b>${formatNumber(remaining)} ${process.satuan || ''}</b>.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'Tetap simpan', cancelButtonText: 'Ubah nilai', confirmButtonColor: '#d97706',
      });
      if (!anomaly.isConfirmed) return handleNormalResult(nik, process);
    }
    if (process.requires_stitch === true) {
      await submitResultWithStitch(
        nik, process, null, qty, isPercentage ? 1 : 0,
        `Hasil <b>${formatNumber(qty)}${isPercentage ? '%' : ` ${process.satuan || ''}`}</b> untuk proses <b>${process.proses}</b>.<br>Masukkan Jumlah Stitch:`,
      );
    } else {
      await submitResult(nik, process, null, qty, isPercentage ? 1 : 0);
    }
  };

  const handleRecordResult = async () => {
    if (!selectedProcess || actionLoading) return;
    const process = selectedProcess;
    const nik = await askNik('Catat hasil');
    if (!nik) return focusScanner();
    if (process.is_bordir === true) await handleEmbroideryResult(nik, process);
    else await handleNormalResult(nik, process);
  };

  const processStatus = (process: Proses) => {
    const target = Number(process.qty || 0); const result = Number(process.qty_hasil || 0);
    if (target > 0 && result >= target) return { key: 'complete', label: 'Target tercapai', icon: 'mdi:check-circle' };
    if (process.tgl_awal) return { key: 'running', label: result > 0 ? 'Hasil sebagian' : 'Sedang berjalan', icon: 'mdi:progress-clock' };
    return { key: 'waiting', label: 'Belum dimulai', icon: 'mdi:clock-outline' };
  };

  const historyHasEmbroidery = history.some(row => row.is_bordir === true || Number(row.is_bordir) === 1);

  return <div className="machine-input-page">
    <Card className="machine-period-card">
      <Box><Typography variant="h4" fontWeight={850}>Input Produksi {station || 'Mesin'}</Typography><Typography color="text.secondary">Scan WO, pilih proses, lalu catat hasil produksi.</Typography></Box>
      <Box className="machine-period-fields"><TextField size="small" type="date" label="Tanggal awal WO" value={awal} onChange={event => { setAwal(event.target.value); setPeriodError(''); }} InputLabelProps={{ shrink: true }} /><TextField size="small" type="date" label="Tanggal akhir WO" value={akhir} onChange={event => { setAkhir(event.target.value); setPeriodError(''); }} InputLabelProps={{ shrink: true }} /><Button variant="contained" onClick={applyPeriod} disabled={workOrderLoading} startIcon={<Icon icon="mdi:calendar-filter" />}>Tampilkan WO</Button></Box>
      {periodError && <Alert severity="warning" className="machine-period-error">{periodError}</Alert>}
    </Card>

    <Box className="machine-workflow">
      <Card className="machine-panel wo-panel">
        <Box className="machine-panel-heading"><Box className="step-number">1</Box><Box><Typography variant="h6" fontWeight={800}>Scan Work Order</Typography><Typography variant="body2" color="text.secondary">Periode {formatDate(appliedPeriod.awal)} – {formatDate(appliedPeriod.akhir)}</Typography></Box><Chip size="small" label={`${workOrders.length} WO`} variant="outlined" /></Box>
        <TextField inputRef={scanRef} autoFocus fullWidth placeholder="Scan kode WO lalu tekan Enter..." value={scanValue} onChange={event => { setScanValue(event.target.value); setScanError(''); }} onKeyDown={handleScan} error={Boolean(scanError)} helperText={scanError || 'Scanner barcode biasanya mengirim Enter secara otomatis.'} InputProps={{ startAdornment: <Icon icon="mdi:barcode-scan" className="scan-icon" /> }} />
        {workOrderError && <Alert severity="error" action={<Button color="inherit" size="small" onClick={getWorkOrders}>Coba lagi</Button>}>{workOrderError}</Alert>}
        <Box className="machine-card-list">
          {workOrderLoading ? Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} variant="rounded" height={108} />) : visibleWorkOrders.map(workOrder => <button type="button" key={workOrder.id} className={`wo-card ${selectedWO?.id === workOrder.id ? 'selected' : ''}`} onClick={() => selectWorkOrder(workOrder)}><Box><strong>{workOrder.kode || `WO #${workOrder.id}`}</strong><span>{workOrder.product || 'Produk tidak tersedia'}</span></Box><Box className="wo-card-meta"><span>{formatNumber(workOrder.qty)} {workOrder.satuan || '-'}</span><Icon icon="mdi:chevron-right" /></Box></button>)}
          {!workOrderLoading && !workOrderError && visibleWorkOrders.length === 0 && <Box className="machine-empty"><Icon icon="mdi:barcode-off" /><strong>WO tidak ditemukan</strong><span>Periksa kode atau ubah periode tanggal.</span></Box>}
        </Box>
      </Card>

      <Card className="machine-panel process-panel">
        <Box className="machine-panel-heading"><Box className="step-number">2</Box><Box><Typography variant="h6" fontWeight={800}>Pilih Proses</Typography><Typography variant="body2" color="text.secondary">Hanya proses yang diizinkan untuk stasiun ini.</Typography></Box></Box>
        {processNotice && <Alert severity="warning" sx={{ mb: 1.5 }}>{processNotice}</Alert>}
        {selectedWO && <Alert severity="info" className="selected-wo-info" icon={<Icon icon="mdi:package-variant" />}><strong>{selectedWO.kode || `WO #${selectedWO.id}`}</strong> — {selectedWO.product || '-'}</Alert>}
        <Box className="machine-card-list">
          {processLoading ? Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} variant="rounded" height={112} />) : selectedWO ? processes.map(process => {
            const status = processStatus(process);
            return <button type="button" key={process.id} className={`process-card ${status.key} ${selectedProcess?.id === process.id ? 'selected' : ''}`} onClick={() => { setSelectedProcess(process); getHistory(process); }}><Box className="process-card-title"><strong>{process.proses || `Proses ${process.urutan}`}</strong><span className={`process-status ${status.key}`}><Icon icon={status.icon} />{status.label}</span></Box><Box className="process-quantity"><span>Hasil <b>{formatNumber(process.qty_hasil)}</b></span><span>Target <b>{formatNumber(process.qty)}</b> {process.satuan || ''}</span></Box>{process.tgl_awal && <small>Mulai {formatDateTime(process.tgl_awal)}</small>}</button>;
          }) : <Box className="machine-empty"><Icon icon="mdi:package-variant-closed" /><strong>Belum ada WO dipilih</strong><span>Scan atau pilih work order terlebih dahulu.</span></Box>}
          {!processLoading && selectedWO && processes.length === 0 && <Box className="machine-empty"><Icon icon="mdi:shield-alert-outline" /><strong>Tidak ada proses yang diizinkan</strong><span>WO ini tidak memiliki proses untuk stasiun {station || 'aktif'}.</span></Box>}
        </Box>
      </Card>

      <Card className="machine-panel action-panel">
        <Box className="machine-panel-heading"><Box className="step-number">3</Box><Box><Typography variant="h6" fontWeight={800}>Mulai / Catat Hasil</Typography><Typography variant="body2" color="text.secondary">Periksa WO dan proses sebelum melanjutkan.</Typography></Box></Box>
        {selectedWO && selectedProcess ? <>
          <Box className="action-summary"><Box><span>Work order</span><strong>{selectedWO.kode || `WO #${selectedWO.id}`}</strong></Box><Box><span>Produk</span><strong>{selectedWO.product || '-'}</strong></Box><Box><span>Proses</span><strong>{selectedProcess.proses}</strong></Box><Box><span>Target / hasil</span><strong>{formatNumber(selectedProcess.qty)} / {formatNumber(selectedProcess.qty_hasil)} {selectedProcess.satuan || ''}</strong></Box></Box>
          {!selectedProcess.tgl_awal ? <Button className="primary-machine-action start" size="large" variant="contained" disabled={actionLoading} onClick={handleStartProcess} startIcon={actionLoading ? <CircularProgress size={21} color="inherit" /> : <Icon icon="mdi:play-circle" />}>{actionLoading ? 'Memproses...' : 'Mulai Proses'}</Button> : <Button className="primary-machine-action result" size="large" variant="contained" disabled={actionLoading} onClick={handleRecordResult} startIcon={actionLoading ? <CircularProgress size={21} color="inherit" /> : <Icon icon="mdi:check-circle-outline" />}>{actionLoading ? 'Menyimpan...' : 'Catat Hasil'}</Button>}
          <Box className="history-section"><Box className="history-heading"><Typography fontWeight={800}>Riwayat Hasil</Typography>{history.length > 0 && <Chip size="small" label={`${history.length} catatan`} />}</Box>{historyLoading ? <Skeleton variant="rounded" height={160} /> : history.length ? <Box className="history-table-wrap"><table><thead><tr><th>ID</th><th>Qty</th>{historyHasEmbroidery && <th>Jumlah Stitch</th>}<th>PIC</th><th>Waktu</th></tr></thead><tbody>{history.map((row, index) => { const rowIsEmbroidery = row.is_bordir === true || Number(row.is_bordir) === 1; return <tr key={row.id ?? index}><td>{row.id ?? '-'}</td><td>{formatNumber(row.qty)}</td>{historyHasEmbroidery && <td>{rowIsEmbroidery && row.stitch != null ? formatNumber(row.stitch) : '-'}</td>}<td><strong>{row.pic || '-'}</strong></td><td>{formatDateTime(row.tgl_selesai)}</td></tr>; })}</tbody></table></Box> : <Box className="history-empty">Belum ada hasil yang dicatat untuk proses ini.</Box>}</Box>
        </> : <Box className="machine-empty action-empty"><Icon icon="mdi:gesture-tap" /><strong>Siap menerima input</strong><span>Pilih WO dan proses untuk menampilkan tombol aksi.</span></Box>}
      </Card>
    </Box>
  </div>;
}
